import { useInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { type CompanyListItem, companiesQueryOptions } from '@/entities/company'
import { ArchiveCompanyDialog, useArchiveCompany } from '@/features/archive-company'
import { CreateCompanyDialog } from '@/features/create-company'
import { EditCompanyDialogLazy } from '@/features/edit-company'
import { useToggleCompanyVisibility } from '@/features/toggle-visibility'
import {
  bySlice,
  cn,
  flattenPages,
  type ListSlice,
  listSliceFilters,
  listSliceOptions,
  parseListSlice,
  pluralRu,
} from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { Menu } from '@/shared/ui/menu'
import { PageHeader } from '@/shared/ui/page-header'
import { Segmented } from '@/shared/ui/segmented'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { Table, TBody, TD, TH, THead, TR } from '@/shared/ui/table'

interface CompaniesSearch {
  slice?: ListSlice
}

export const Route = createFileRoute('/_app/companies/')({
  beforeLoad: ({ context }) => {
    if (context.session?.kind === 'customer') throw redirect({ to: '/projects' })
  },
  head: () => ({ meta: [{ title: 'Компании · STIT Deal' }] }),
  // Срез живёт в URL (шарится, переживает перезагрузку). Дефолт (active) в схему
  // не пишем — URL чистый.
  validateSearch: (s: Record<string, unknown>): CompaniesSearch => {
    const slice = parseListSlice(s.slice)
    return slice && slice !== 'active' ? { slice } : {}
  },
  component: CompaniesPage,
})

function CompanyRow({
  company,
  hidden,
  onEdit,
  onArchive,
  onUnarchive,
}: {
  company: CompanyListItem
  hidden: boolean
  onEdit: () => void
  onArchive: () => void
  onUnarchive: () => void
}) {
  const toggleVisibility = useToggleCompanyVisibility(company.id)
  const archived = company.archived
  return (
    <TR className={cn((hidden || archived) && 'opacity-60')}>
      <TD>
        <span className="flex items-center gap-2">
          <Link
            to="/companies/$company"
            params={{ company: company.slug }}
            className="font-medium text-text-primary hover:text-accent hover:underline"
          >
            {company.name}
          </Link>
          {hidden && <span className="label shrink-0 text-text-tertiary">скрыта в deal</span>}
          {archived && <span className="label shrink-0 text-text-tertiary">в архиве</span>}
        </span>
      </TD>
      <TD className="font-mono text-text-tertiary text-xs">{company.slug}</TD>
      <TD className="text-right text-text-secondary">
        {company.project_count > 0
          ? `${company.project_count} ${pluralRu(company.project_count, 'проект', 'проекта', 'проектов')}`
          : '—'}
      </TD>
      <TD className="text-right">
        <Menu
          aria-label={`Действия с «${company.name}»`}
          trigger={<Icon name="dots" size={18} />}
          triggerClassName="p-1.5"
          items={[
            { label: 'Изменить', icon: 'pencil', onSelect: onEdit },
            {
              label: hidden ? 'Показать в deal' : 'Скрыть в deal',
              icon: 'eye',
              onSelect: () => toggleVisibility.mutate(!hidden),
            },
            archived
              ? { label: 'Вернуть из архива', icon: 'check', onSelect: onUnarchive }
              : { label: 'В архив', icon: 'collapse', onSelect: onArchive },
          ]}
        />
      </TD>
    </TR>
  )
}

function CompaniesPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const slice = search.slice ?? 'active'
  // Набор под срез: «активные» — узкая серверная выборка, остальные срезы
  // считаются из полной.
  const listQ = useInfiniteQuery(companiesQueryOptions(listSliceFilters(slice)))
  // CompanyView не несёт hidden — скрытые выводим разницей с видимым набором.
  // Архивные в нём нужны, иначе архивная видимая компания сошла бы за скрытую.
  const visibleQ = useInfiniteQuery(companiesQueryOptions({ includeArchived: true }))
  const [createOpen, setCreateOpen] = useState(false)
  const [editSlug, setEditSlug] = useState<string | null>(null)
  const archive = useArchiveCompany()
  const [archiveTarget, setArchiveTarget] = useState<CompanyListItem | null>(null)

  const visibleIds = new Set(flattenPages(visibleQ.data?.pages).map((c) => c.id))
  const isHidden = (id: string) => !visibleIds.has(id)
  const companies = bySlice(flattenPages(listQ.data?.pages), slice, (c) => isHidden(c.id))

  return (
    <div className="p-4 md:p-6">
      <PageHeader
        title="Компании"
        actions={
          <>
            <Segmented
              label="Срез списка компаний"
              options={listSliceOptions}
              value={slice}
              onChange={(next) =>
                void navigate({ search: next === 'active' ? {} : { slice: next } })
              }
            />
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Icon name="plus" size={16} />
              Компания
            </Button>
          </>
        }
      />

      {listQ.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : listQ.error ? (
        <StatusScreen title="Не удалось загрузить компании" message="Обновите страницу." />
      ) : companies.length === 0 ? (
        <EmptyState
          title="Компаний нет"
          description={
            slice === 'active' ? 'Заведите первую компанию-заказчика.' : 'В этом срезе пусто.'
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          <Table>
            <THead>
              <TR>
                <TH>Название</TH>
                <TH>Slug</TH>
                <TH className="text-right">Проекты</TH>
                <TH className="w-10">
                  <span className="sr-only">Действия</span>
                </TH>
              </TR>
            </THead>
            <TBody>
              {companies.map((c) => (
                <CompanyRow
                  key={c.id}
                  company={c}
                  hidden={isHidden(c.id)}
                  onEdit={() => setEditSlug(c.slug)}
                  onArchive={() => setArchiveTarget(c)}
                  onUnarchive={() => archive.mutate({ id: c.id, archived: false })}
                />
              ))}
            </TBody>
          </Table>
          {listQ.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void listQ.fetchNextPage()}
                disabled={listQ.isFetchingNextPage}
              >
                {listQ.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}
              </Button>
            </div>
          )}
        </div>
      )}

      <CreateCompanyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(company) =>
          void navigate({ to: '/companies/$company', params: { company: company.slug } })
        }
      />

      {editSlug != null && (
        <EditCompanyDialogLazy slug={editSlug} open onClose={() => setEditSlug(null)} />
      )}

      {archiveTarget != null && (
        <ArchiveCompanyDialog
          company={{
            id: archiveTarget.id,
            name: archiveTarget.name,
            project_count: archiveTarget.project_count,
          }}
          open
          onClose={() => setArchiveTarget(null)}
        />
      )}
    </div>
  )
}
